import { FC, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";

import { negativeState, positiveState } from "@/lib/store";

export type Point = [number, number];

const HORIZONS = [
    { label: "1 week", days: 7 },
    { label: "1 month", days: 30 },
    { label: "3 months", days: 91 },
    { label: "6 months", days: 182 },
    { label: "1 year", days: 365 },
];

/** Change in percent to the latest point from the latest point on or before ``days`` earlier. */
export const changeOver = (series: Point[], days: number): number | null => {
    if (series.length < 2) return null;
    const latest = series[series.length - 1];
    const cutoff = dayjs(latest[0]).subtract(days, "day").valueOf();
    const base = [...series].reverse().find(([time]) => time <= cutoff);
    if (!base || !base[1]) return null;
    return ((latest[1] - base[1]) / Math.abs(base[1])) * 100;
};

const percent = (value: number | null) => (value == null ? "N/A" : `${value > 0 ? "+" : ""}${value.toFixed(1)}%`);

const COLUMNS = "minmax(64px, 1fr) minmax(52px, auto) minmax(52px, auto) minmax(84px, 1.4fr)";

interface Props {
    eps: Point[];
    price: Point[];
    obfuscated: boolean;
    month: { eps: number | null; price: number | null };
}

/** EPS and share price changes side by side, with a bar for how far apart each horizon is. */
const EpsHorizons: FC<Props> = ({ eps, price, obfuscated, month }) => {
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);

    const rows = useMemo(
        () =>
            HORIZONS.map(({ label, days }) => {
                const epsChange = days === 30 ? month.eps : changeOver(eps, days);
                const priceChange = days === 30 ? month.price : changeOver(price, days);
                return { label, epsChange, priceChange, gap: epsChange != null && priceChange != null ? epsChange - priceChange : null };
            }),
        [eps, price, month]
    );

    const widest = Math.max(1, ...rows.map(({ gap }) => Math.abs(gap ?? 0)));
    const tone = (value: number | null) => (value == null || obfuscated ? "text.secondary" : value < 0 ? negative : positive);

    return (
        <Box role="table" aria-label="EPS and share price changes by period" sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-evenly", minHeight: 240, px: 0.5 }}>
            <Box role="row" sx={{ display: "grid", gridTemplateColumns: COLUMNS, columnGap: 1.5 }}>
                {["Period", "EPS", "Price", "Gap"].map((header, i) => (
                    <Typography key={header} role="columnheader" variant="caption" sx={{ color: "text.secondary", textAlign: i === 1 || i === 2 ? "right" : "left" }}>
                        {header}
                    </Typography>
                ))}
            </Box>
            {rows.map(({ label, epsChange, priceChange, gap }) => (
                <Box key={label} role="row" sx={{ display: "grid", gridTemplateColumns: COLUMNS, columnGap: 1.5, alignItems: "center" }}>
                    <Typography role="cell" variant="body2">
                        {label}
                    </Typography>
                    <Typography role="cell" variant="body2" className={obfuscated ? "obfuscated" : undefined} sx={{ textAlign: "right", color: tone(epsChange), fontVariantNumeric: "tabular-nums" }}>
                        {percent(epsChange)}
                    </Typography>
                    <Typography role="cell" variant="body2" className={obfuscated ? "obfuscated" : undefined} sx={{ textAlign: "right", color: tone(priceChange), fontVariantNumeric: "tabular-nums" }}>
                        {percent(priceChange)}
                    </Typography>
                    <Box role="cell" aria-label={gap == null ? "N/A" : `${gap.toFixed(1)} percentage points`} className={obfuscated ? "obfuscated" : undefined} sx={{ position: "relative", height: 14 }}>
                        <Box sx={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: "1px", bgcolor: "divider" }} />
                        {gap != null && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 2,
                                    bottom: 2,
                                    borderRadius: "2px",
                                    bgcolor: tone(gap),
                                    width: `${(Math.abs(gap) / widest) * 50}%`,
                                    ...(gap < 0 ? { right: "50%" } : { left: "50%" }),
                                }}
                            />
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default EpsHorizons;
